import React, { useState, type ReactNode } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { isCetEnabled, type CampaignData, type CampaignUser } from "@/api/campaign";
import type { CreatorEngagementListItem } from "@/api/creatorCpi";
import { isDraft, phaseBadgeStyle, formatLastModified } from "../../utils/campaignListHelpers";
import { getCountryFlagUrl, getCountryShort } from "@/shared/utils/formatters";
import { LoadingIndicator } from "@/shared/components/Loading/Loading";
import { Icon } from "@/shared/components/Icon";
import {
	CustomTable,
	type CustomTableColumn,
	type CustomTableProps,
	type TableFilterState,
} from "@/shared/components/Table/CustomTable";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { PHONE_MAX_WIDTH, TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { CampaignFilterSheet } from "./CampaignFilterSheet";
import { MarketTooltip } from "./MarketTooltip";
import { pressedDarken } from "@/shared/theme/pressed";

export interface FinanceCampaignTableRow {
	campaignId: string;
	projectCode: string;
	campaignName: string;
	brand: string | null;
	markets: string[];
	campaignManager: string | null;
	paidCount: number;
	totalCount: number;
	disbursement: number;
	overallFee: number;
	currency: string;
}

interface Props {
	data?: CampaignData[];
	onRowClick?: (campaign: CampaignData, event: React.MouseEvent<HTMLElement>) => void;
	isClientUser?: boolean;
	isCreatorUser?: boolean;
	/** Admins see whether each campaign runs the CET flow. */
	isAdminUser?: boolean;
	currentPage?: number;
	onPageChange?: (page: number) => void;
	filterState?: TableFilterState;
	onFilterStateChange?: (next: TableFilterState) => void;
	loading?: boolean;
	engagementsByCampaignId?: Record<string, CreatorEngagementListItem>;
	dashboardState?: "error" | "empty";
	onRetry?: () => void;
	financeData?: FinanceCampaignTableRow[];
	onFinanceRowClick?: (campaign: FinanceCampaignTableRow, event: React.MouseEvent<HTMLElement>) => void;
	/** Phone-only actions at the end of the table toolbar, e.g. New Campaign. */
	toolbarEnd?: ReactNode;
}

const MAX_FLAGS = 3;

const compareText = (a?: string, b?: string) =>
	(a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });

const compareDate = (a?: string, b?: string) => {
	const aTime = a ? Date.parse(a) : Number.NaN;
	const bTime = b ? Date.parse(b) : Number.NaN;
	if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
	if (Number.isNaN(aTime)) return 1;
	if (Number.isNaN(bTime)) return -1;
	return aTime - bTime;
};

const CampaignTableShell = styled.div`
	background: #ebeefd;
	border-radius: 12px;
	overflow: hidden;
`;

/** Phone row above the table: actions moved out of the page header, and the Filter sheet. */
const TableToolbar = styled.div`
	display: none;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;

	${mediaPhone`display: flex;`}
`;

const CampaignLoadingPlaceholder = styled.div`
	height: 200px;
`;

const DashboardState = styled.div`
	display: flex;
	min-height: 479px;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 20px;
	padding: 32px 20px;
	text-align: center;

	${mediaPhone`min-height: 0;`}
`;

const DashboardStateBadge = styled.div<{ $error?: boolean }>`
	display: grid;
	place-items: center;
	box-sizing: border-box;
	min-width: ${({ $error }) => ($error ? "50px" : "56px")};
	min-height: ${({ $error }) => ($error ? "50px" : "56px")};
	padding: ${({ $error }) => ($error ? "0" : "16px")};
	width: ${({ $error }) => ($error ? "50px" : "56px")};
	height: ${({ $error }) => ($error ? "50px" : "56px")};
	border-radius: ${({ $error }) => ($error ? "50%" : "64px")};
	background: ${({ $error }) => ($error ? "#fde2ef" : "rgba(180, 148, 253, 0.29)")};
	color: ${({ $error }) => ($error ? "#d80027" : "#3a0ca3")};
`;

const DashboardStateCopy = styled.div`
	display: flex;
	width: min(100%, 480px);
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

const DashboardStateTitle = styled.h2`
	margin: 0;
	color: #303030;
	font-size: 20px;
	font-weight: 600;
	line-height: 30px;
`;

const DashboardStateBody = styled.p`
	margin: 0;
	color: #707070;
	font-size: 15px;
	font-weight: 400;
	line-height: 24px;
`;

const RetryButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 80px;
	width: 107px;
	padding: 8px 12px;
	border: none;
	border-radius: 64px;
	background: #3a0ca3;
	color: #ffffff;
	font: inherit;
	font-size: 16px;
	font-weight: 600;
	line-height: normal;
	cursor: pointer;

	${pressedDarken}
`;

const MarketsList = styled.span`
	display: flex;
	align-items: center;
	gap: 4px;
`;

const MarketItem = styled.span`
	display: inline-flex;
	align-items: center;
`;

const MarketFlagImage = styled.img`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	object-fit: cover;
	display: block;
`;

const MarketFallbackFlag = styled.span`
	font-size: 14px;
`;

const MarketExtraCount = styled.span`
	color: #888;
	font-size: 13px;
	margin-left: 2px;
`;

const ProjectCodeCell = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	overflow: hidden;
`;

const ProjectCodeText = styled.span`
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
	font-size: 14px;
	font-weight: 500;
	color: #454545;
`;

const DraftTag = styled.span`
	background: #f0f0f0;
	color: #666;
	padding: 1px 8px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	flex-shrink: 0;
`;

const CampaignNameBrandCell = styled.div`
	min-width: 0;

	/* Phone card: name and brand on the left, status (and Draft) on the right. */
	${mediaPhone`
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		column-gap: 12px;
		align-items: center;

		> * {
			grid-column: 1;
		}
	`}
`;

const CampaignNameText = styled.div`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
	font-weight: 600;
	font-size: 14px;
	color: #303030;
`;

const CampaignSubText = styled.div`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
	color: #8b8c91;
	font-size: 12px;
	font-weight: 400;

	/* Phone rows size to content: reserve the line so brand-less rows keep the same height. */
	${mediaPhone`min-height: 1.5em;`}
`;

/** Details from hidden columns, shown under the name when the table collapses to one column. */
const PhoneMeta = styled.div`
	display: none;

	/* Right-hand column of the phone card, centred against the name and brand lines. */
	${mediaPhone`
		display: flex;
		grid-column: 2;
		grid-row: 1 / span 2;
		align-items: center;
		justify-content: flex-end;
		gap: 6px;
		min-width: 0;
		max-width: 50vw;

		> * {
			flex: 0 1 auto;
			width: auto;
		}
	`}
`;

const StatusBadgeCell = styled.div`
	min-width: 0;
	width: 100%;
`;

const StatusBadgeText = styled.span`
	display: inline-block;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: middle;
`;

const StatusStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const CreatorStatusTag = styled.span<{
	$tone:
		| "pending"
		| "production"
		| "verification"
		| "payment"
		| "done"
		| "declined"
		| "muted"
		| "warn"
		| "unavailable";
}>`
	display: inline-flex;
	align-items: center;
	width: fit-content;
	max-width: 100%;
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 500;
	line-height: normal;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	${({ $tone }) => {
		switch ($tone) {
			case "pending":
			case "unavailable":
				return "background: #dde2fa; color: #454545;";
			case "production":
				return "background: #ffe3ac; color: #bb7f05;";
			case "verification":
				return "background: #abe4f6; color: #0182ab;";
			case "payment":
				return "background: #f6ffed; color: #43890f; border: 1px solid #89d04a;";
			case "done":
				return "background: #bfef90; color: #377608;";
			case "declined":
				return "background: #f8d4d9; color: #c00021;";
			case "warn":
				return "background: #fef2f2; color: #b91c1c;";
			default:
				return "background: #f0f0f0; color: #666;";
		}
	}}
`;

const DeliverablesCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
`;

const ProgressTrack = styled.div`
	width: 100%;
	max-width: 140px;
	height: 8px;
	border-radius: 999px;
	background: #e7edfb;
	overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $tone: "empty" | "partial" | "complete" }>`
	height: 100%;
	width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
	border-radius: 999px;
	background: ${({ $tone }) => {
		switch ($tone) {
			case "complete":
				return "#6aab35";
			case "partial":
				return "#f7a825";
			default:
				return "transparent";
		}
	}};
`;

const ProgressLabel = styled.div`
	font-size: 13px;
	font-weight: 500;
	color: #8b8c91;
`;

const LastModifiedDate = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #454545;
	line-height: 20px;
`;

const BrandRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	margin-top: 2px;
`;

const BrandFlag = styled.img`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const BrandFlagFallback = styled.span`
	font-size: 12px;
	line-height: 1;
	flex-shrink: 0;
`;

/** First market's flag beside the brand, on phones where the Markets column is hidden. */
const PhoneBrandFlag = styled(BrandFlag)`
	display: none;

	${mediaPhone`
		display: inline-block;
		margin-right: 6px;
		vertical-align: -2px;
	`}
`;

const CampaignTableStyles = createGlobalStyle`
	.campaign-table {
		--campaign-bg: #ebeefd;
		--campaign-header-bg: #dde2fa;
		--campaign-header-text: #011992;
		--campaign-cell-bg: rgba(255, 255, 255, 0.6);
		--campaign-row-hover: #ffffff;
		--campaign-table-sep-head: #BDC6F2;
		--campaign-table-sep-body: #E6E6E6;
		--campaign-pagination-text: #2f3138;
		--campaign-pagination-muted: #454545;
		--campaign-pagination-disabled: #a8acb8;
		--campaign-pagination-pressed-bg: #efefef;
	}

	.campaign-table .ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
		width: 80px !important;
		height: 55px !important;
		margin: -27.5px 0 0 -40px !important;
	}

	/* Ant Design adds a white pseudo-element over blurred loading content. */
	.campaign-table .ant-spin-container::after {
		background: transparent !important;
	}

	.campaign-table .ant-table-thead > tr > th {
		background: var(--campaign-header-bg) !important;
		color: var(--campaign-header-text) !important;
		font-family: "Poppins", sans-serif !important;
		font-weight: 600 !important;
		font-size: 14px !important;
		line-height: 24px !important;
		vertical-align: middle !important;
		border: none !important;
		height: 56px;
		padding: 12px 0 !important;
		position: relative;
	}

	.campaign-table .ant-table-thead > tr > th:not(:last-child)::after {
		content: "";
		position: absolute;
		right: 0;
		top: 25%;
		height: 43.8%;
		width: 1px;
		background-color: var(--campaign-table-sep-head);
    	z-index: 99 !important; 
	}

	.campaign-table .ant-table-thead > tr > th::before {
		display: none !important;
	}

	.campaign-table .ant-table-tbody > tr > td {
		background: var(--campaign-cell-bg) !important;
		border: none !important;
		padding: 12px 16px !important;
		height: 74px !important;
		min-height: 74px !important;
		position: relative;
	}

	.campaign-table .ant-table-tbody > tr > td:not(:last-child)::after {
		content: "";
		position: absolute;
		right: 0;
		top: 25%;
		height: 56.7%;
		width: 1px;
		background-color: var(--campaign-table-sep-body);
		z-index: 99 !important;
	}

	.campaign-table .ant-table-tbody > tr > .ant-table-cell:first-child {
		border-radius: 16px 0 0 16px !important;
		padding-left: 24px !important;
	}

	.campaign-table .ant-table-thead > tr > .ant-table-cell:first-child {
		border-radius: 16px 0 0 16px !important;
		padding-left: 8px !important;
	}

	.campaign-table .ant-table-tbody > tr > .ant-table-cell:last-child,
	.campaign-table .ant-table-thead > tr > .ant-table-cell:last-child {
		border-radius: 0 16px 16px 0 !important;
		padding-right: 24px !important;
	}

	.campaign-table .ant-table-tbody > tr:hover > td {
		background: var(--campaign-row-hover) !important;
	}

	/* Touch: a tapped row fades to near-transparent while pressed (the browser's own tap flash is
	   turned off globally). */
	.campaign-table .ant-table-tbody > tr:active > td {
		background: rgba(255, 255, 255, 0.2) !important;
	}

	.campaign-table .ant-table-tbody > tr.creator-campaign-unavailable > td {
		cursor: pointer;
	}

	.campaign-table .ant-table-tbody > tr.creator-campaign-unavailable > td:not(:nth-child(3)) > * {
		opacity: 0.4;
	}

	.campaign-table.creator-dashboard-empty .ant-table-thead {
		opacity: 0.6;
	}

	.campaign-table.creator-dashboard-empty .ant-table-tbody > tr > td,
	.campaign-table.creator-dashboard-empty .ant-table-tbody > tr:hover > td,
	.campaign-table.creator-dashboard-empty .ant-table-placeholder,
	.campaign-table.creator-dashboard-empty .ant-table-placeholder > td,
	.campaign-table.creator-dashboard-empty .ant-empty {
		background: transparent !important;
		border: none !important;
		box-shadow: none !important;
		height: auto !important;
		min-height: 0 !important;
		padding: 0 !important;
	}

	.campaign-table.creator-dashboard-empty .ant-table-tbody > tr > td::after {
		content: none !important;
	}

	.campaign-table.creator-dashboard-empty .ant-table-tbody > tr > .ant-table-cell:first-child,
	.campaign-table.creator-dashboard-empty .ant-table-tbody > tr > .ant-table-cell:last-child {
		border-radius: 0 !important;
	}

	.campaign-table.creator-dashboard-empty table {
		border-spacing: 0 !important;
	}

	.campaign-table .ant-table {
		background: var(--campaign-bg) !important;
	}

	.campaign-table table {
		border-collapse: separate !important;
		border-spacing: 0 4px !important;
	}

	.campaign-table .ant-table-thead {
		position: relative !important;
	}

	.campaign-table .ant-table-thead::after {
		content: "";
		display: block;
		height: 0;
	}

	.campaign-table .ant-table-pagination.ant-pagination {
		display: flex !important;
		justify-content: flex-start;
		align-items: center;
		gap: 4px;
		margin: 18px 0 0 !important;
		padding-right: 4px;
		opacity: 1;
	}

	.campaign-table .ant-pagination-item,
	.campaign-table .ant-pagination-prev,
	.campaign-table .ant-pagination-next,
	.campaign-table .ant-pagination-jump-prev,
	.campaign-table .ant-pagination-jump-next {
		margin-inline-end: 0 !important;
		height: 40px;
		min-height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: visible;
	}

	.campaign-table .ant-pagination-item,
	.campaign-table .ant-pagination-item-active,
	.campaign-table .ant-pagination-jump-prev,
	.campaign-table .ant-pagination-jump-next,
	.campaign-table .ant-pagination-prev .ant-pagination-item-link,
	.campaign-table .ant-pagination-next .ant-pagination-item-link {
		width: 56px;
		min-width: 56px;
		height: 40px;
		padding: 8px 12px;
		border-radius: 64px;
	}

	.campaign-table .ant-pagination-item,
	.campaign-table .ant-pagination-item-active,
	.campaign-table .ant-pagination-prev .ant-pagination-item-link,
	.campaign-table .ant-pagination-next .ant-pagination-item-link,
	.campaign-table .ant-pagination-jump-prev,
	.campaign-table .ant-pagination-jump-next {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.campaign-table .ant-pagination-item {
		line-height: 24px;
		border: none !important;
		background: transparent;
		opacity: 1;
		gap: 4px;
		font-size: 14px;
		font-weight: 500;
		color: var(--campaign-pagination-text);
	}

	.campaign-table .ant-pagination-item a {
		color: inherit;
	}

	.campaign-table .ant-pagination-item:hover {
		background: var(--campaign-cell-bg) !important;
	}

	.campaign-table .ant-pagination-item:active {
		background: var(--campaign-pagination-pressed-bg) !important;
		color: var(--color-pink) !important;
	}

	.campaign-table .ant-pagination-item:active a {
		color: var(--color-pink) !important;
	}

	.campaign-table .ant-pagination-item-active {
		background: var(--campaign-cell-bg) !important;
		color: var(--color-pink) !important;
	}

	.campaign-table .ant-pagination-item-active a {
		color: var(--color-pink) !important;
	}

	.campaign-table .ant-pagination-jump-prev,
	.campaign-table .ant-pagination-jump-next {
		background: transparent;
		opacity: 1;
		color: var(--campaign-pagination-muted);
		gap: 4px;
	}

	.campaign-table .ant-pagination-jump-prev:hover,
	.campaign-table .ant-pagination-jump-next:hover {
		background: var(--campaign-cell-bg) !important;
	}

	.campaign-table .ant-pagination-jump-prev:active,
	.campaign-table .ant-pagination-jump-next:active {
		background: var(--campaign-pagination-pressed-bg) !important;
	}

	.campaign-table .ant-pagination-jump-prev:active .ant-pagination-item-link-icon,
	.campaign-table .ant-pagination-jump-next:active .ant-pagination-item-link-icon,
	.campaign-table .ant-pagination-jump-prev:active .ant-pagination-item-ellipsis,
	.campaign-table .ant-pagination-jump-next:active .ant-pagination-item-ellipsis {
		color: var(--color-pink) !important;
	}

	.campaign-table .ant-pagination-jump-prev .ant-pagination-item-container,
	.campaign-table .ant-pagination-jump-next .ant-pagination-item-container {
		position: relative !important;
		display: block !important;
		width: 100% !important;
		min-height: 24px !important;
	}

	.campaign-table .ant-pagination-jump-prev .ant-pagination-item-ellipsis,
	.campaign-table .ant-pagination-jump-next .ant-pagination-item-ellipsis,
	.campaign-table .ant-pagination-jump-prev .ant-pagination-item-link-icon,
	.campaign-table .ant-pagination-jump-next .ant-pagination-item-link-icon {
		position: absolute !important;
		top: 50% !important;
		left: 50% !important;
		transform: translate(-50%, -50%) !important;
		margin: 0 !important;
		inset-inline: auto !important;
		line-height: 1 !important;
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
	}

	.campaign-table .ant-pagination-jump-prev .ant-pagination-item-ellipsis,
	.campaign-table .ant-pagination-jump-next .ant-pagination-item-ellipsis {
		letter-spacing: 0 !important;
		text-indent: 0 !important;
		font-size: 16px;
		color: var(--campaign-pagination-muted) !important;
	}

	.campaign-table .ant-pagination-jump-prev .ant-pagination-item-link-icon,
	.campaign-table .ant-pagination-jump-next .ant-pagination-item-link-icon {
		font-size: 16px !important;
		font-weight: 900 !important;
		color: var(--campaign-pagination-muted) !important;
		-webkit-text-stroke: 3px currentColor;
	}

	.campaign-table .ant-pagination-prev .ant-pagination-item-link,
	.campaign-table .ant-pagination-next .ant-pagination-item-link {
		gap: 4px;
		border: none !important;
		background: transparent;
		opacity: 1;
		color: var(--campaign-pagination-text);
		font-size: 16px;
		transition: background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
	}

	.campaign-table .ant-pagination-prev:hover .ant-pagination-item-link,
	.campaign-table .ant-pagination-next:hover .ant-pagination-item-link {
		background: var(--campaign-cell-bg) !important;
	}

	.campaign-table .ant-pagination-prev:active .ant-pagination-item-link,
	.campaign-table .ant-pagination-next:active .ant-pagination-item-link {
		background: var(--campaign-pagination-pressed-bg) !important;
		color: var(--color-pink) !important;
		transform: none;
	}

	.campaign-table .ant-pagination-disabled .ant-pagination-item-link,
	.campaign-table .ant-pagination-disabled:hover .ant-pagination-item-link {
		background: transparent;
		color: var(--campaign-pagination-disabled) !important;
		transform: none;
	}

	/* Phone: one full-width column (CustomTable phone mode); rows grow with their content. */
	${mediaPhone`
		.campaign-table .ant-table-tbody > tr > td {
			height: auto !important;
			min-height: 64px !important;
			padding: 12px 16px !important;
		}

		.campaign-table .ant-table-tbody > tr > .ant-table-cell:only-child,
		.campaign-table .ant-table-thead > tr > .ant-table-cell:only-child {
			border-radius: 16px !important;
			padding-left: 16px !important;
			padding-right: 16px !important;
		}

		.campaign-table .ant-pagination-simple .ant-pagination-prev,
		.campaign-table .ant-pagination-simple .ant-pagination-next {
			min-width: ${TAP_TARGET}px;
			height: ${TAP_TARGET}px;
		}

		/* The pager row is as tall as its 44px page box, with room below so the table
		   shell's overflow clip can't cut the focus ring. */
		.campaign-table .ant-table-pagination.ant-pagination-simple {
			height: auto;
			min-height: ${TAP_TARGET}px;
			margin-bottom: 4px !important;
			padding-bottom: 4px;
		}

		/* The page box mirrors the desktop pager's active page: a pink number on the row tint. */
		.campaign-table .ant-pagination-simple .ant-pagination-simple-pager {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			height: ${TAP_TARGET}px;
			color: var(--campaign-pagination-muted);
			font-size: 14px;
			font-weight: 500;
		}

		.campaign-table .ant-pagination-simple .ant-pagination-simple-pager input {
			width: 56px;
			height: ${TAP_TARGET}px;
			margin: 0;
			padding: 0;
			border: none !important;
			border-radius: 999px;
			background: var(--campaign-cell-bg);
			color: var(--color-pink);
			font-size: 16px;
			font-weight: 600;
			text-align: center;
			box-shadow: none;
			transition: box-shadow 0.18s ease, background-color 0.18s ease;
		}

		.campaign-table .ant-pagination-simple .ant-pagination-simple-pager input:hover {
			background: var(--campaign-row-hover);
		}

		.campaign-table .ant-pagination-simple .ant-pagination-simple-pager input:focus {
			outline: none;
			background: var(--campaign-row-hover);
			/* Inset, so the table shell's overflow: hidden can't clip the ring. */
			box-shadow: inset 0 0 0 2px #3a0ca3;
		}

		.campaign-table .ant-pagination-simple .ant-pagination-slash {
			margin: 0;
			color: var(--campaign-pagination-muted);
		}
	`}
`;

/**
 * Shared campaign table surface. Feature views provide their own typed columns,
 * while spacing, loading, pagination and row styling stay consistent.
 */
export function CampaignStyledTable<T extends object>({
	className,
	loading,
	locale,
	pagination,
	filterState: externalFilterState,
	onFilterStateChange,
	toolbarEnd,
	...props
}: CustomTableProps<T> & {
	/** Phone-only actions shown at the end of the toolbar row (e.g. New Campaign). */
	toolbarEnd?: ReactNode;
}) {
	// Hold filter state here when the caller doesn't, so the phone filter sheet and the
	// header dropdowns share it.
	const [localFilterState, setLocalFilterState] = useState<TableFilterState>({});
	const filterState = externalFilterState ?? localFilterState;
	const setFilterState = onFilterStateChange ?? setLocalFilterState;
	const columns = props.columns ?? [];
	const hasPrimaryColumn = columns.some((column) => column.mobile === "primary");

	// Phones get antd's compact "‹ 2 / 9 ›" pager instead of a row of page numbers.
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const tablePagination = isPhone && pagination ? { ...pagination, simple: true } : pagination;

	const isBooleanLoading = typeof loading === "boolean";
	const brandedLoading = isBooleanLoading
		? { spinning: loading, indicator: <LoadingIndicator width={80} height={55} /> }
		: loading;
	const loadingLocale =
		isBooleanLoading && loading
			? { ...locale, emptyText: <CampaignLoadingPlaceholder aria-hidden="true" /> }
			: locale;

	return (
		<>
			{hasPrimaryColumn && (
				<TableToolbar>
					<CampaignFilterSheet<T>
						columns={columns}
						dataSource={props.dataSource}
						filterState={filterState}
						onFilterStateChange={setFilterState}
					/>
					{toolbarEnd}
				</TableToolbar>
			)}
			<CampaignTableShell className={`campaign-table ${className || ""}`}>
				<CampaignTableStyles />
				<CustomTable<T>
					{...props}
					className={className}
					loading={brandedLoading}
					locale={loadingLocale}
					pagination={tablePagination}
					filterState={filterState}
					onFilterStateChange={setFilterState}
					fitColumns={hasPrimaryColumn}
				/>
			</CampaignTableShell>
		</>
	);
}

const renderMarkets = (markets: Array<{ name: string; status_phase: string }> | undefined) => {
	if (!markets?.length) return <p style={{ color: "#707070" }}>No market</p>;
	const shown = markets.slice(0, MAX_FLAGS);
	const extra = markets.length - MAX_FLAGS;
	return (
		<MarketsList>
			{shown.map((m, i) => (
				<MarketTooltip key={i} name={m.name} statusPhase={m.status_phase}>
					<MarketItem>
						{getCountryFlagUrl(m.name) ? (
							<MarketFlagImage src={getCountryFlagUrl(m.name)} alt={getCountryShort(m.name)} />
						) : (
							<MarketFallbackFlag>🏳️</MarketFallbackFlag>
						)}
					</MarketItem>
				</MarketTooltip>
			))}
			{extra > 0 && <MarketExtraCount>+{extra}</MarketExtraCount>}
		</MarketsList>
	);
};

export const CampaignMarkets = ({ markets }: { markets: string[] }) =>
	renderMarkets(markets.map((name) => ({ name, status_phase: "" })));

export const CampaignTable: React.FC<Props> = ({
	data = [],
	onRowClick,
	isClientUser = false,
	isCreatorUser = false,
	isAdminUser = false,
	currentPage = 1,
	onPageChange,
	filterState,
	onFilterStateChange,
	loading,
	engagementsByCampaignId,
	dashboardState,
	onRetry,
	financeData,
	onFinanceRowClick,
	toolbarEnd,
}) => {
	if (financeData) {
		const formatFinanceAmount = (amount: number, currency: string) =>
			`${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(amount)} ${currency}`;
		const financeColumns: CustomTableColumn<FinanceCampaignTableRow>[] = [
			{
				title: "Project Code",
				dataIndex: "projectCode",
				key: "projectCode",
				width: 176,
				enableSort: true,
				enableFilter: false,
				sortLabelType: "text",
				sorter: (a, b) => compareText(a.projectCode, b.projectCode),
				render: (value: string) => <ProjectCodeText>{value}</ProjectCodeText>,
			},
			{
				title: "Campaign Name",
				dataIndex: "campaignName",
				key: "campaignName",
				width: 240,
				mobile: "primary",
				enableSort: true,
				enableFilter: false,
				sortLabelType: "text",
				sorter: (a, b) => compareText(a.campaignName, b.campaignName),
				render: (_: string, record) => (
					<CampaignNameBrandCell>
						<CampaignNameText>{record.campaignName}</CampaignNameText>
						<CampaignSubText>{record.brand || "—"}</CampaignSubText>
					</CampaignNameBrandCell>
				),
			},
			{
				title: "Markets",
				dataIndex: "markets",
				key: "markets",
				width: 176,
				enableSort: false,
				enableFilter: true,
				render: (_: unknown, record) => <CampaignMarkets markets={record.markets} />,
			},
			{
				title: "Campaign Manager",
				dataIndex: "campaignManager",
				key: "campaignManager",
				width: 176,
				enableSort: true,
				enableFilter: true,
				sortLabelType: "text",
				sorter: (a, b) => compareText(a.campaignManager ?? undefined, b.campaignManager ?? undefined),
				render: (value: string | null) => value || "—",
			},
			{
				title: "Creators Invoices",
				key: "invoiceProgress",
				width: 176,
				enableSort: true,
				enableFilter: false,
				sortLabelType: "number",
				sorter: (a, b) => {
					const aProgress = a.totalCount ? a.paidCount / a.totalCount : 0;
					const bProgress = b.totalCount ? b.paidCount / b.totalCount : 0;
					return aProgress - bProgress || a.paidCount - b.paidCount;
				},
				render: (_: unknown, record) => {
					const percentage = record.totalCount ? (record.paidCount / record.totalCount) * 100 : 0;
					return (
						<DeliverablesCell>
							<ProgressTrack>
								<ProgressFill
									$pct={percentage}
									$tone={percentage >= 100 ? "complete" : percentage > 0 ? "partial" : "empty"}
								/>
							</ProgressTrack>
							<ProgressLabel>
								{record.paidCount} out of {record.totalCount} paid
							</ProgressLabel>
						</DeliverablesCell>
					);
				},
			},
			{
				title: "Disbursement",
				key: "disbursement",
				width: 176,
				enableSort: false,
				enableFilter: false,
				render: (_: unknown, record) => formatFinanceAmount(record.disbursement, record.currency),
			},
			{
				title: "Overall Fee",
				key: "overallFee",
				width: 176,
				enableSort: false,
				enableFilter: false,
				render: (_: unknown, record) => formatFinanceAmount(record.overallFee, record.currency),
			},
		];

		return (
			<CampaignStyledTable<FinanceCampaignTableRow>
				className="finance-campaign-table"
				columns={financeColumns}
				dataSource={financeData}
				loading={loading}
				pagination={{
					current: currentPage,
					pageSize: 7,
					showSizeChanger: false,
					onChange: (page) => onPageChange?.(page),
				}}
				size="middle"
				rowKey={(record) => `${record.campaignId}-${record.currency}`}
				onRow={(record) => ({
					onClick: (event) => onFinanceRowClick?.(record, event),
					style: { cursor: "pointer", background: "transparent" },
				})}
			/>
		);
	}

	const formatBudget = (r: CampaignData) => {
		const rate = r.totalBudget;
		const currency = r.currency;
		if (rate == null || typeof rate !== "number" || Number.isNaN(rate)) return "-";
		if (!currency) return `${rate.toLocaleString()}`;
		return `${rate.toLocaleString()} ${currency}`;
	};

	const renderStatus = (phase: string) => {
		return (
			<StatusBadgeCell>
				<StatusBadgeText style={phaseBadgeStyle(phase)} title={phase}>
					{phase}
				</StatusBadgeText>
			</StatusBadgeCell>
		);
	};

	const marketsColumn = (width: number): CustomTableColumn<CampaignData> => ({
		title: "Markets",
		dataIndex: "markets",
		key: "markets",
		width,
		enableFilter: true,
		enableSort: false,
		render: (_: unknown, r: CampaignData) => renderMarkets(r.markets),
	});

	const statusColumn = (width: number): CustomTableColumn<CampaignData> => ({
		title: "Status",
		dataIndex: "statusPhase",
		key: "statusPhase",
		width,
		enableFilter: true,
		enableSort: false,
		render: (phase: string) => renderStatus(phase),
	});

	const lastModifiedColumn = (width: number): CustomTableColumn<CampaignData> => ({
		title: "Last Modified",
		dataIndex: "lastModificationTime",
		key: "lastModified",
		width,
		enableFilter: false,
		enableSort: true,
		sortLabelType: "date",
		sorter: (a, b) => compareDate(a.lastModificationTime, b.lastModificationTime),
		render: (date: string) => formatLastModified(date),
	});

	const campaignNameColumn = (width: number): CustomTableColumn<CampaignData> => ({
		title: "Campaign Name",
		dataIndex: "name",
		key: "name",
		width,
		mobile: "primary",
		ellipsis: true,
		enableFilter: false,
		enableSort: true,
		sortLabelType: "text",
		sorter: (a, b) => compareText(a.name, b.name),
		render: (_: string, r: CampaignData) => (
			<CampaignNameBrandCell>
				<CampaignNameText title={r.name}>{r.name}</CampaignNameText>
				<PhoneMeta>{renderStatus(r.statusPhase)}</PhoneMeta>
			</CampaignNameBrandCell>
		),
	});

	const totalBudgetColumn = (): CustomTableColumn<CampaignData> => ({
		title: "Total Budget",
		key: "totalBudget",
		width: 160,
		enableFilter: false,
		enableSort: true,
		sortLabelType: "number",
		sorter: (a, b) => (a.finalRate ?? Number.NEGATIVE_INFINITY) - (b.finalRate ?? Number.NEGATIVE_INFINITY),
		render: (_: unknown, r: CampaignData) => formatBudget(r),
	});

	const cetColumn: CustomTableColumn<CampaignData> = {
		title: "CET",
		dataIndex: "cetEnabled",
		key: "cetEnabled",
		width: 80,
		enableFilter: false,
		enableSort: false,
		render: (_: boolean | undefined, campaign: CampaignData) =>
			isCetEnabled(campaign) ? (
				<CreatorStatusTag $tone="done">On</CreatorStatusTag>
			) : (
				<CreatorStatusTag $tone="muted">Off</CreatorStatusTag>
			),
	};

	const columns: CustomTableColumn<CampaignData>[] = [
		{
			title: "Project Code",
			dataIndex: "projectCode",
			key: "projectCode",
			width: 160,
			enableFilter: false,
			enableSort: true,
			sortLabelType: "text",
			sorter: (a, b) => compareText(a.projectCode, b.projectCode),
			render: (code: string, r: CampaignData) => (
				<ProjectCodeCell>
					<ProjectCodeText>{code}</ProjectCodeText>
					{isDraft(r) && <DraftTag>Draft</DraftTag>}
				</ProjectCodeCell>
			),
		},
		{
			title: "Campaign Name/Brand",
			dataIndex: "name",
			key: "name",
			width: 200,
			mobile: "primary",
			ellipsis: true,
			enableFilter: false,
			enableSort: true,
			sortLabelType: "text",
			sorter: (a, b) => compareText(a.name, b.name),
			render: (_: string, r: CampaignData) => {
				const clientNames = getClientNames(r);
				const firstMarket = r.markets?.[0]?.name;
				const firstMarketFlag = firstMarket ? getCountryFlagUrl(firstMarket) : "";
				return (
					<CampaignNameBrandCell>
						<CampaignNameText title={r.name}>{r.name}</CampaignNameText>
						<CampaignSubText title={clientNames}>
							{firstMarketFlag && <PhoneBrandFlag src={firstMarketFlag} alt="" />}
							{clientNames}
						</CampaignSubText>
						<PhoneMeta>
							{isDraft(r) && <DraftTag>Draft</DraftTag>}
							{renderStatus(r.statusPhase)}
						</PhoneMeta>
					</CampaignNameBrandCell>
				);
			},
		},
		{
			...marketsColumn(140),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 100,
			enableFilter: true,
			enableSort: false,
			render: (t: string) => t || "-",
		},
		{
			...totalBudgetColumn(),
		},
		{
			...statusColumn(130),
		},
		...(isAdminUser ? [cetColumn] : []),
		{
			title: "Lead",
			dataIndex: "lead",
			key: "lead",
			width: 140,
			enableFilter: true,
			enableSort: false,
			render: (l: CampaignUser[]) => l?.map((u) => u.name).join(", ") || "-",
		},
		{
			...lastModifiedColumn(150),
		},
	];

	const clientColumns: CustomTableColumn<CampaignData>[] = [
		campaignNameColumn(260),
		marketsColumn(160),
		totalBudgetColumn(),
		statusColumn(150),
		lastModifiedColumn(160),
	];

	// Removed creators: the engagement says so, or (campaigns without CET, which have no engagement) the list does.
	const isUnavailableToCreator = (campaign: CampaignData) =>
		campaign.viewerRole === "Creator" &&
		(engagementsByCampaignId?.[campaign.campaignId]?.isAvailable ?? campaign.isAvailable) === false;

	const renderEngagementStatus = (campaign: CampaignData) => {
		const engagement = engagementsByCampaignId?.[campaign.campaignId];
		if (isUnavailableToCreator(campaign)) {
			return <CreatorStatusTag $tone="unavailable">No longer available</CreatorStatusTag>;
		}
		if (!engagement) {
			return <span style={{ color: "#999" }}>—</span>;
		}

		if (engagement.status !== "declined" && (engagement.canAcceptTerms || engagement.status === "pending_terms")) {
			return (
				<StatusStack>
					<CreatorStatusTag $tone="pending">
						{engagement.latestVersionNumber
							? `V${engagement.latestVersionNumber} - Pending Acceptance`
							: "Pending Acceptance"}
					</CreatorStatusTag>
				</StatusStack>
			);
		}

		const statusMap: Record<
			string,
			{
				label: string;
				tone: "pending" | "production" | "verification" | "payment" | "done" | "declined" | "muted" | "warn";
			}
		> = {
			terms_accepted: { label: "Ongoing Production", tone: "production" },
			in_progress: { label: "Ongoing Production", tone: "production" },
			deliverables_submitted: { label: "Deliverables Verification", tone: "verification" },
			cm_approved: { label: "Deliverables Verification", tone: "verification" },
			sent_to_finance: { label: "Processing Payment", tone: "payment" },
			completed: { label: "Done", tone: "done" },
			on_hold: { label: "On hold", tone: "muted" },
			cancelled: { label: "Cancelled", tone: "muted" },
			declined: { label: "Creator Declined", tone: "declined" },
			legacy: { label: "Done", tone: "done" },
		};
		const mapped = statusMap[engagement.status] || {
			label: engagement.status.replace(/_/g, " "),
			tone: "muted" as const,
		};

		return (
			<StatusStack>
				<CreatorStatusTag $tone={mapped.tone}>{mapped.label}</CreatorStatusTag>
			</StatusStack>
		);
	};

	const renderDeliverablesProgress = (campaign: CampaignData) => {
		const engagement = engagementsByCampaignId?.[campaign.campaignId];
		if (!engagement) {
			return <ProgressLabel style={{ color: "#999" }}>—</ProgressLabel>;
		}

		const total = engagement.deliverablesTotal ?? 0;
		const done = engagement.deliverablesDone ?? 0;
		if (total <= 0) {
			return <ProgressLabel style={{ color: "#999" }}>—</ProgressLabel>;
		}

		const pct = (done / total) * 100;
		const tone = done <= 0 ? "empty" : done >= total ? "complete" : "partial";

		return (
			<DeliverablesCell>
				<ProgressTrack>
					<ProgressFill $pct={pct} $tone={tone} />
				</ProgressTrack>
				<ProgressLabel>
					{done} out of {total} done
				</ProgressLabel>
			</DeliverablesCell>
		);
	};

	const renderCreatorLastModified = (date: string) => {
		if (!date) return "—";
		try {
			const d = new Date(date);
			if (Number.isNaN(d.getTime())) return date;
			return (
				<LastModifiedDate>
					{d.toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
					})}
				</LastModifiedDate>
			);
		} catch {
			return date;
		}
	};

	const creatorColumns: CustomTableColumn<CampaignData>[] = [
		{
			title: "Project Code",
			dataIndex: "projectCode",
			key: "projectCode",
			width: 160,
			enableFilter: false,
			enableSort: true,
			sortLabelType: "text",
			sorter: (a, b) => compareText(a.projectCode, b.projectCode),
			render: (code: string, r: CampaignData) => {
				const engagement = engagementsByCampaignId?.[r.campaignId];
				const displayCode = engagement?.projectCode || code;
				return <ProjectCodeText title={displayCode || undefined}>{displayCode || "—"}</ProjectCodeText>;
			},
		},
		{
			title: "Campaign Name",
			dataIndex: "name",
			key: "name",
			width: 280,
			mobile: "primary",
			ellipsis: true,
			enableFilter: false,
			enableSort: true,
			sortLabelType: "text",
			sorter: (a, b) => compareText(a.name, b.name),
			render: (_: string, r: CampaignData) => {
				const brandOrClient = getBrandOrClientNames(r);
				const market = r.markets?.[0]?.name;
				const flagUrl = market ? getCountryFlagUrl(market) : "";
				return (
					<CampaignNameBrandCell>
						<CampaignNameText title={r.name}>{r.name}</CampaignNameText>
						<BrandRow>
							{market &&
								(flagUrl ? (
									<BrandFlag src={flagUrl} alt="" />
								) : (
									<BrandFlagFallback>{getCountryShort(market) || "🏳️"}</BrandFlagFallback>
								))}
							<CampaignSubText title={brandOrClient}>{brandOrClient || "—"}</CampaignSubText>
						</BrandRow>
						<PhoneMeta>{renderEngagementStatus(r)}</PhoneMeta>
					</CampaignNameBrandCell>
				);
			},
		},
		{
			title: "Status",
			key: "engagementStatus",
			width: 200,
			enableFilter: false,
			enableSort: false,
			render: (_: unknown, record: CampaignData) => renderEngagementStatus(record),
		},
		{
			title: "Deliverables",
			key: "deliverablesProgress",
			width: 160,
			enableFilter: false,
			enableSort: false,
			render: (_: unknown, record: CampaignData) => renderDeliverablesProgress(record),
		},
		{
			title: "Last Modified",
			dataIndex: "lastModificationTime",
			key: "lastModified",
			width: 140,
			enableFilter: false,
			enableSort: true,
			sortLabelType: "date",
			sorter: (a, b) => compareDate(a.lastModificationTime, b.lastModificationTime),
			render: (date: string) => renderCreatorLastModified(date),
		},
	];

	const getClientNames = (campaign: CampaignData) =>
		campaign.client?.filter((c) => typeof c === "string").join(", ") || "";

	const getBrandOrClientNames = (campaign: CampaignData) => {
		const brands = campaign.brand?.filter((b) => typeof b === "string").join(", ");
		if (brands) return brands;
		return getClientNames(campaign);
	};

	return (
		<CampaignStyledTable<CampaignData>
			toolbarEnd={toolbarEnd}
			className={dashboardState ? "creator-dashboard-empty" : undefined}
			columns={isClientUser ? clientColumns : isCreatorUser ? creatorColumns : columns}
			dataSource={data}
			loading={{ spinning: loading ?? false, indicator: <LoadingIndicator width={80} height={55} /> }}
			locale={
				loading
					? { emptyText: <CampaignLoadingPlaceholder aria-hidden="true" /> }
					: dashboardState === "error"
						? {
								emptyText: (
									<DashboardState>
										<DashboardStateBadge $error>
											<Icon name="xCircle" size={22.5} />
										</DashboardStateBadge>
										<DashboardStateCopy>
											<DashboardStateTitle>We couldn't load your campaigns</DashboardStateTitle>
											<DashboardStateBody>
												Something went wrong on our end. Nothing has changed in your campaigns —
												give it another go.
											</DashboardStateBody>
										</DashboardStateCopy>
										<RetryButton type="button" onClick={onRetry}>
											Retry
										</RetryButton>
									</DashboardState>
								),
							}
						: dashboardState === "empty"
							? {
									emptyText: (
										<DashboardState>
											<DashboardStateBadge>
												<Icon name="flag02" size={24} preserveOriginalColor />
											</DashboardStateBadge>
											<DashboardStateCopy>
												<DashboardStateTitle>No campaigns yet</DashboardStateTitle>
												<DashboardStateBody>
													This is where your campaigns will live. Your Campaigns Team will
													invite you to your first one soon — and we'll email you the moment
													it lands.
												</DashboardStateBody>
											</DashboardStateCopy>
										</DashboardState>
									),
								}
							: undefined
			}
			pagination={
				dashboardState
					? false
					: {
							current: currentPage,
							pageSize: 7,
							showSizeChanger: false,
							onChange: (page) => onPageChange?.(page),
						}
			}
			size="middle"
			filterState={filterState}
			onFilterStateChange={onFilterStateChange}
			rowKey="campaignId"
			rowClassName={(record) => (isUnavailableToCreator(record) ? "creator-campaign-unavailable" : "")}
			onRow={(record) => ({
				onClick: (event) => onRowClick?.(record, event),
				onKeyDown: (event) => {
					if (isUnavailableToCreator(record) && (event.key === "Enter" || event.key === " ")) {
						event.preventDefault();
						onRowClick?.(record, event as unknown as React.MouseEvent<HTMLElement>);
					}
				},
				"aria-disabled": isUnavailableToCreator(record) || undefined,
				tabIndex: isUnavailableToCreator(record) ? 0 : undefined,
				style: { cursor: "pointer", background: "transparent" },
			})}
		/>
	);
};
